// MF-568 — Shared logic of the PAT cards: band ordering / colours, the class rows of CURRENT_FIELDS, medians,
// keyboard-friendly button props. Nothing here computes a stanine: it is read from the row or it is N/A.
import { useEffect, useRef } from 'react'
import { benchmarkColor } from '../boardPalettes'

export const STANINE_NOTE = "Stanines need the school's norms (Board Builder → PAT settings)."
export const INTERPRETATION =
  'Scale scores measure achievement on the learning-area scale. Stanines describe relative standing against a named norm group; stanine change is not learning growth. Achievement bands describe ranges of the scale and can remain unchanged while scores rise.'

const num = (v) =>
  v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v)
const RANGE = /(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/

const parseBand = (band) => {
  const m = String(band ?? '').match(RANGE)
  return m ? { lo: Number(m[1]), hi: Number(m[2]) } : { lo: null, hi: null }
}

/** Keyboard + mouse activation (Enter / Space) for a non-button element. */
export const buttonProps = (onActivate, pressed) => ({
  role: 'button',
  tabIndex: 0,
  ...(pressed === undefined ? {} : { 'aria-pressed': !!pressed }),
  onClick: onActivate,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onActivate(e)
    }
  }
})

export const median = (values) => {
  const v = values.filter((x) => x !== null && x !== undefined).sort((a, b) => a - b)
  if (!v.length) return null
  const m = Math.floor(v.length / 2)
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2
}

export const fmtDay = (iso) => {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso ? String(iso) : '–'
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * Class rows → { students (valid score, best first), total, excluded, noStanine, bands, dates, yearLevels, hasStanine }.
 * Bands come from the data, ordered by their lower bound; gaps between occupied 10-point bands are filled so the
 * scale reads continuously. Low bands get the orange end of the scale, high bands the blue end.
 */
export const prepareClass = (rows = []) => {
  const all = rows.map((r, i) => ({
    ...r,
    id: String(r.student_id ?? `${r.name}-${i}`),
    score: num(r.score),
    stanine: num(r.stanine)
  }))
  const students = all
    .filter((s) => s.score !== null)
    .sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name)))

  const found = new Map()
  for (const s of students) {
    const key = s.band ? String(s.band) : 'No band'
    s.bandKey = key
    if (!found.has(key)) found.set(key, { key, ...parseBand(key) })
  }
  let list = [...found.values()].sort(
    (a, b) => (a.lo ?? Infinity) - (b.lo ?? Infinity) || a.key.localeCompare(b.key)
  )
  const parsed = list.filter((b) => b.lo !== null)
  if (parsed.length > 1) {
    const step = parsed[0].hi - parsed[0].lo + 1
    if (step > 0) {
      const filled = []
      for (let i = 0; i < parsed.length; i += 1) {
        filled.push(parsed[i])
        const next = parsed[i + 1]
        let lo = parsed[i].lo + step
        while (next && lo < next.lo && filled.length < 60) {
          filled.push({ key: `${lo}-${lo + step - 1}`, lo, hi: lo + step - 1 })
          lo += step
        }
      }
      list = [...filled, ...list.filter((b) => b.lo === null)]
    }
  }
  const n = list.length
  const bands = list.map((b, i) => {
    const color = benchmarkColor((n === 1 ? 0.5 : i / (n - 1)) * 100)
    return { ...b, index: i, color, tint: `${color}2e` }
  })
  return {
    students,
    total: all.length,
    excluded: all.length - students.length,
    noStanine: students.filter((s) => s.stanine === null).length,
    hasStanine: students.some((s) => s.stanine !== null),
    bands,
    dates: [...new Set(students.map((s) => s.test_date).filter(Boolean))],
    yearLevels: [...new Set(students.map((s) => s.year_level).filter(Boolean))]
  }
}

/** ECharts measures its box once: follow the tile when it is resized. */
export const useChartResize = () => {
  const chartRef = useRef(null)
  const boxRef = useRef(null)
  useEffect(() => {
    const el = boxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => chartRef.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { chartRef, boxRef }
}
